import { observable, observe } from '@legendapp/state';
import { supabase } from '../lib/supabase';
import { auth$ } from './auth$';
import { isAuthed$ } from './syncHelpers';
import { platform } from '../lib/native';
import { captureError, ErrorCategory } from '../lib/errorHandler';
import { trackEvent } from '../lib/analytics';
import {
  checkCreditThresholds,
  KEY_ALERTED_500,
  KEY_ALERTED_100,
  KEY_ALERTED_DEPLETED,
} from '../utils/creditThresholds';

// ─── SessionStorage cache (subscription only — mirrors previous useSubscription behavior) ───

const CACHE_KEY = 'reedle_sub';

function readSubCache() {
  try { return JSON.parse(sessionStorage.getItem(CACHE_KEY)); } catch { return null; }
}
function writeSubCache(sub) {
  try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(sub)); } catch { /* ignore */ }
}
function clearSubCache() {
  try { sessionStorage.removeItem(CACHE_KEY); } catch { /* ignore */ }
}

// ─── Observables — pre-seeded from cache for instant cold-start render ───

const initialCache = readSubCache();

export const subscription$ = observable({
  data: initialCache || null,
  loading: !initialCache,
  error: null,
});

export const creditBalance$ = observable({
  balance: null,
  tierCredits: null,
  permanentCredits: null,
  loading: true,
  error: null,
});

// ─── Module-level state (one set per app, not per component) ───

let realtimeChannel = null;
let subPollInterval = null;
let creditPollInterval = null;
let visibilityHandler = null;
let appResumeHandler = null;
let prevBalance = null;

// ─── Fetch functions ───

export async function fetchSubscription() {
  const userId = auth$.user.peek()?.id;
  if (!userId) {
    subscription$.loading.set(false);
    return;
  }

  try {
    const { data, error: rpcError } = await supabase.rpc('reedle_subscription_get', {
      p_user_id: userId,
    });

    if (rpcError) {
      console.error('[billing$] Error fetching subscription:', rpcError);
      subscription$.assign({
        error: captureError(rpcError, { component: 'billing$', action: 'fetch_subscription', category: ErrorCategory.PAYMENT }),
        loading: false,
      });
      return;
    }

    if (data?.success && data?.subscription) {
      writeSubCache(data.subscription);
      subscription$.assign({ data: data.subscription, loading: false, error: null });
    } else {
      // Free tier fallback — same defaults as previous useSubscription
      const fallback = {
        tier: 'free',
        status: 'active',
        tier_credit_balance: 80,
        current_period_start: null,
        current_period_end: null,
        renewal_mode: 'none',
        is_vip: false,
        days_remaining: null,
        trial_end: null,
      };
      writeSubCache(fallback);
      subscription$.assign({ data: fallback, loading: false, error: null });
    }
  } catch (err) {
    console.error('[billing$] fetchSubscription threw:', err);
    subscription$.assign({
      error: captureError(err, { component: 'billing$', action: 'fetch_subscription', category: ErrorCategory.PAYMENT }),
      loading: false,
    });
  }
}

export async function fetchBalance() {
  const userId = auth$.user.peek()?.id;
  if (!userId) {
    creditBalance$.loading.set(false);
    return;
  }

  try {
    const { data, error } = await supabase.rpc('reedle_subscription_balance', {
      p_user_id: userId,
    });

    if (error) {
      console.error('[billing$] Error fetching credit balance:', error);
      creditBalance$.assign({ error, loading: false });
      return;
    }

    const newBalance = data?.total_balance ?? null;
    const newTierCredits = data?.tier_credits ?? null;
    const newPermanentCredits = data?.permanent_credits ?? null;

    // Credit threshold detection — runs in singleton, no race condition
    const alertedFlags = {
      alerted500: !!localStorage.getItem(KEY_ALERTED_500),
      alerted100: !!localStorage.getItem(KEY_ALERTED_100),
      alertedDepleted: !!localStorage.getItem(KEY_ALERTED_DEPLETED),
    };

    const { events, flagsToSet, flagsToClear } = checkCreditThresholds(
      newBalance, prevBalance, alertedFlags
    );

    for (const key of flagsToClear) {
      localStorage.removeItem(key);
      console.log(`[billing$] Reset ${key} flag after top-up`);
    }

    for (let i = 0; i < events.length; i++) {
      trackEvent(events[i], { balance: newBalance, user_id: userId });
      localStorage.setItem(flagsToSet[i], 'true');
      console.log(`[billing$] Fired ${events[i]} event`);
    }

    prevBalance = newBalance;
    creditBalance$.assign({
      balance: newBalance,
      tierCredits: newTierCredits,
      permanentCredits: newPermanentCredits,
      loading: false,
      error: null,
    });
  } catch (err) {
    console.error('[billing$] fetchBalance threw:', err);
    creditBalance$.assign({ error: err, loading: false });
  }
}

// ─── Realtime subscription — one channel for the entire app lifetime ───

function setupRealtime(userId) {
  if (realtimeChannel) return; // already set up

  realtimeChannel = supabase
    .channel(`subscription:${userId}`)
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'reedle_user_subscribe',
        filter: `user_id=eq.${userId}`,
      },
      () => {
        fetchSubscription();
      }
    )
    .subscribe((status) => {
      if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
        console.warn('[billing$] Realtime connection issue, polling will continue');
      }
    });
}

// ─── Init / Cleanup ───

function initBilling(userId) {
  // Fetch immediately
  fetchSubscription();
  fetchBalance();

  // Single Realtime channel for instant Stripe webhook-triggered updates
  setupRealtime(userId);

  // Polling fallbacks — Realtime can disconnect silently on tab background
  subPollInterval = setInterval(fetchSubscription, 300000);   // 5 min: subscription
  creditPollInterval = setInterval(fetchBalance, 30000);       // 30 sec: credits (server-side deductions)

  // Refresh subscription on tab focus — catches updates missed while offline/backgrounded
  visibilityHandler = () => {
    if (document.visibilityState === 'visible') {
      fetchSubscription();
    }
  };
  document.addEventListener('visibilitychange', visibilityHandler);

  // On native: refresh both balance and subscription when app returns to foreground
  // (e.g. after user manages subscription in App Store).
  // native.js dispatches 'app:resume' via Capacitor appStateChange — web never fires this.
  if (platform.isNative) {
    appResumeHandler = () => {
      fetchBalance();
      fetchSubscription();
    };
    document.addEventListener('app:resume', appResumeHandler);
  }
}

function cleanupBilling() {
  if (realtimeChannel) {
    realtimeChannel.unsubscribe();
    realtimeChannel = null;
  }
  if (subPollInterval) {
    clearInterval(subPollInterval);
    subPollInterval = null;
  }
  if (creditPollInterval) {
    clearInterval(creditPollInterval);
    creditPollInterval = null;
  }
  if (visibilityHandler) {
    document.removeEventListener('visibilitychange', visibilityHandler);
    visibilityHandler = null;
  }
  if (appResumeHandler) {
    document.removeEventListener('app:resume', appResumeHandler);
    appResumeHandler = null;
  }

  clearSubCache();
  prevBalance = null;

  subscription$.assign({ data: null, loading: true, error: null });
  creditBalance$.assign({ balance: null, tierCredits: null, permanentCredits: null, loading: true, error: null });
}

// ─── Auth-gated initialization via isAuthed$ ───
// Single-callback form fires on first run, correctly handling the case
// where billing$.js is loaded lazily after isAuthed$ is already true.
// Else-branch cleanup mirrors the pattern used in contentFetcher.js.

observe(() => {
  const isAuthed = isAuthed$.get();
  if (isAuthed) {
    const userId = auth$.user.peek()?.id; // peek — do not track auth$.user as dependency
    if (!userId) return;
    initBilling(userId);
  } else {
    cleanupBilling();
  }
});
